import { useEffect, useRef, useState } from 'react';
import styled from 'libs/styled';
import useBreakpoint from '@sportson/core-web/hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';
import { above } from '@sportson/core-web/utils/mediaqueries';
import { transformBackgroundImageGroup } from 'libs/wordpress/utils/transformBackground';
import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';
import { capitalize } from '@sportson/core-web/utils/string';
import { show as showOverlay } from '@sportson/core-web/state/application/overlay';
import { useLocation } from 'react-router-dom';

import BoxButton from 'components/BoxButton';
import Button from 'components/Button';
import Text from 'components/Text';
import MenuIcon from 'assets/media/icons/Menu';

import { ArticleCollectionCard } from 'components/Article/ArticleCollectionCard';
import useAppDispatch from '@sportson/core-web/hooks/useAppDispatch';
import getQueryParams from '@sportson/core-web/utils/getQueryParams';
import { inServer } from '@sportson/core-web/constants';

const Div = styled('div')``;

const ArticleFilters = styled('div')`
    display: flex;
    gap: 12px;
    margin-bottom: 40px;

    /* Needed to override styling for selected (active) button */
    & button:disabled {
        color: var(--color-base-white) !important;
    }

    & button > p:first-letter {
        text-transform: capitalize;
    }
`;

const ArticleSubcategories = styled('div')`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    grid-column: 1 / -1;
`;

const ArticleGrid = styled('div')`
    padding-top: 0;
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;

    ${above['tablet.lg']} {
        grid-template-columns: repeat(3, 1fr);
        gap: 0 12px;
        column-gap: 24px;
    }
`;

export const ArticleCollectionLayout = ({ isSearchPage = false, data, categories, title, onCategoryChange }) => {
    const isMobile = useBreakpoint({ to: 'tablet.sm' });
    const device = isMobile ? 'mobile' : 'desktop';
    const { t } = useTranslation();
    const wrapperRef = useRef();
    const dispatch = useAppDispatch();
    const url = !inServer && new URL(window.location);
    const location = useLocation();
    const query = getQueryParams(location)?.category;
    const { posts, hitsCount, hits } = data;

    const [selectedCategory, setSelected] = useState('');
    const [initialCategories, setInitialCategories] = useState(categories);
    const [initialHitCount, setInitialHitCount] = useState(null);

    const cleanArticleResponseData = (response) => {
        const camelResponse = objectKeysToCamelCase(response, {
            recursive: true,
            modifyInputObject: false,
        });

        const { title, primaryCategory, category, heroContent, url } = camelResponse;

        return {
            url,
            tag: primaryCategory || category[0] || '',
            title: title || heroContent?.articleSummary?.alternativeTitle,
            image: transformBackgroundImageGroup(heroContent?.articleImage?.backgroundImage, device),
        };
    };

    const scrollToTop = (next, smooth = true) => {
        if (wrapperRef && wrapperRef.current) {
            scrollTo({
                smooth,
                top: wrapperRef.current.offsetTop,
            });
        }
    };

    const handleCategoryChange = (value) => {
        setSelected(value);
        if (onCategoryChange) {
            onCategoryChange(value);
        }
        if (!inServer) {
            url.searchParams.delete('page');
            if (!value) {
                url.searchParams.delete('category');
                window.history.pushState({}, '', url);
                return;
            }
            url.searchParams.set('category', value);
            window.history.pushState({}, '', url);
        }
    };

    useEffect(() => {
        setInitialCategories(categories);
        setInitialHitCount(hitsCount);

        if (query) {
            setSelected(query);
            onCategoryChange(query);
            scrollToTop(0, false);
        }
    }, [categories, query]);

    const showArticleMenu = () => {
        dispatch(
            showOverlay({
                view: 'articlesCollectionOverlay',
                transition: 'from-left',
                data: { initialCategories, handleCategoryChange },
            }),
        );
    };

    return (
        <Div p={['16px 12px', null, '16px 12px', null, null, '16px 40px']} ref={wrapperRef}>
            {title && (
                <Text
                    typography={['Content/32_120_0_500', null, null, null, 'Content/24_130_0_450']}
                    marginBottom="24px"
                >
                    {title}
                </Text>
            )}
            {!isSearchPage && (
                <ArticleFilters>
                    {isMobile ? (
                        <BoxButton size="large" onClick={() => showArticleMenu()}>
                            <MenuIcon />
                            <Text>{t('categories')}</Text>
                        </BoxButton>
                    ) : (
                        <ArticleSubcategories>
                            <Button
                                disabled={selectedCategory === ''}
                                theme={selectedCategory === '' ? 'secondaryDark' : 'secondary'}
                                onClick={() => handleCategoryChange('')}
                                typography="UI/16_100_0_450"
                            >
                                {t('articles.show_all_with_hits', {
                                    hitsCount: initialHitCount,
                                })}
                                {/* TODO: NOTE: Hits count is not working as intended, use this translation but get the correct count per button/category */}
                            </Button>
                            {initialCategories &&
                                categories &&
                                Array.isArray(initialCategories) &&
                                initialCategories.map((category) => (
                                    <Button
                                        key={category}
                                        value={category}
                                        disabled={selectedCategory === category}
                                        theme={selectedCategory === category ? 'secondaryDark' : 'secondary'}
                                        onClick={() => handleCategoryChange(category)}
                                        typography="UI/16_100_0_450"
                                    >
                                        <span>{capitalize(category)}</span>
                                    </Button>
                                ))}
                        </ArticleSubcategories>
                    )}
                </ArticleFilters>
            )}
            <ArticleGrid>
                {isSearchPage &&
                    hits &&
                    hits?.map((p) => (
                        <ArticleCollectionCard
                            key={p?.heading}
                            url={p?.link}
                            tag={p?.category}
                            title={p?.heading}
                            image={transformBackgroundImageGroup(p?.backgroundImage, device)}
                        />
                    ))}
                {posts && posts?.map((p) => <ArticleCollectionCard key={p?.title} {...cleanArticleResponseData(p)} />)}
            </ArticleGrid>
        </Div>
    );
};
